import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { dateUtil, useUser } from "@sdeapps/react-core";
import ControlledDateTime from "components/inputs/ControlledDateTime";
import ControlledTextField from "components/inputs/ControlledTextField";
import type { Commune, Mandat, Perimetre, Territoire } from "models";
import { useFormContext, useWatch } from "react-hook-form";
import ClearIcon from "@mui/icons-material/Clear";
import SaveIcon from "@mui/icons-material/Save";
import CompetenceChip from "components/Typography/CompetenceChip";
import type Fonction from "../../models/Fonction";
import fonctions from "../../constants/fonctions";
import NewMandatFonctionsAutoComplete from "./NewMandatFunctionAutoComplete";
import { competencesAssociationList } from "constants/CompetenceMap";
import { validDateRules } from "constants/InputRules";
import { ApplicationRole } from "constants/ApplicationRole";
import Fonctions from "constants/Fonctions";
import type { MandatSdeaFormModel } from "../../models/Mandat";
import { useMandatData } from "pages/Elu/providers/MandatDataProvider";
import InputSkeleton from "components/InputSkeleton";
import { elusUtil } from "utils";
import ControlledCommuneAutoComplete from "components/inputs/ControlledCommuneAutoComplete";
import ControlledPerimetreAutoComplete from "components/inputs/ControlledPerimetreAutoComplete";
import ControlledTerritoireAutoComplete from "components/inputs/ControlledTerritoireAutoComplete";

function toFormModel(
  mandat: Mandat,
  communes: Array<Commune>,
  perimetres: Array<Perimetre>,
  territoires: Array<Territoire>
): MandatSdeaFormModel {
  return {
    dateDebutMandat: dateUtil.fromFormatToDate(mandat.dateDebutMandat, "yyyy-MM-dd"),
    dateFinMandat:
      mandat.dateFinMandat == null
        ? undefined
        : dateUtil.fromFormatToDate(mandat.dateFinMandat, "yyyy-MM-dd"),
    fonction: {
      id: mandat.idFonction,
      libelle: mandat.libelleFonction ?? "",
      displayFields: fonctions.find((f) => f.id === mandat.idFonction)?.displayFields ?? {},
    },
    competence: mandat.competence,
    territoire: territoires.find((t) => t.id === mandat.idTerritoire),
    perimetre: perimetres.find((p) => p.id === mandat.idPerimetre),
    commune: communes.find((c) => c.id === mandat.codeCollectivite),
  };
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const fonctionDelegueAG = fonctions.find(
  (fonction) => fonction.id === Fonctions.DELEGUE_ASSEMBLEE_GENERALE
)!;

interface MandatSdeaFormProps {
  mandat?: Mandat;
  cancel?: () => void;
  isSending?: boolean;
  sendData: (e?: React.BaseSyntheticEvent) => Promise<void>;
  variant: "create" | "edit";
}

function MandatSdeaTableRowFormContent({
  mandat,
  cancel,
  isSending = false,
  sendData,
  variant,
}: Readonly<MandatSdeaFormProps>): ReactNode {
  const { communes, isLoading, perimetres, territoires } = useMandatData();

  const [availablePerimetres, setAvailablePerimetres] = useState<Array<Perimetre>>([]);

  const { competence, territoire, fonction } = useWatch<MandatSdeaFormModel>();

  const { reset } = useFormContext();

  useEffect(() => {
    if (mandat != null && !isLoading) {
      reset(toFormModel(mandat, communes, perimetres, territoires));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mandat, isLoading, perimetres, territoires, communes]);

  useEffect(() => {
    let tempPerimetres = [...perimetres];

    if (competence != null) {
      tempPerimetres = tempPerimetres.filter((p) => p.competence === competence);
    }
    if (territoire != null) {
      tempPerimetres = tempPerimetres.filter((p) => p?.territoire?.id === territoire.id);
    }

    setAvailablePerimetres(tempPerimetres);
  }, [competence, perimetres, territoire]);

  const { isRoles } = useUser();
  let availableFonctions: Array<Fonction> = [];
  if (isRoles(ApplicationRole.ADMINISTRATOR)) {
    availableFonctions = fonctions;
  } else if (isRoles(ApplicationRole.GESTIONNAIRE_ELECTIONS)) {
    availableFonctions = [fonctionDelegueAG];
  } else {
    availableFonctions = [];
  }

  if (isLoading) {
    return (
      <TableRow>
        <TableCell colSpan={5}>
          <InputSkeleton />
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell width="400px" padding="none" sx={{ p: 0.5 }}>
        {variant === "create" ? (
          <NewMandatFonctionsAutoComplete
            fonctions={availableFonctions}
            name="fonction"
            label="Fonction *"
            rules={{ required: "Choisir une fonction est obligatoire" }}
          />
        ) : (
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          <Typography>{elusUtil.getLibelleFonction(mandat!)}</Typography>
        )}
      </TableCell>
      <TableCell width="170px" padding="none" sx={{ p: 0.5 }}>
        {fonction?.displayFields?.competence === true && (
          <ControlledTextField
            select
            name="competence"
            label="Compétence"
            sx={{ background: "white" }}>
            {/* On gère explicitement ce cas plus tard dans mandatsUtil.getMandatForCompareFromFormModel */}
            <MenuItem value="">Aucune</MenuItem>
            {competencesAssociationList.map((c) => (
              <MenuItem key={c.competence} value={c.competence}>
                <CompetenceChip competence={c.competence} />
              </MenuItem>
            ))}
          </ControlledTextField>
        )}
      </TableCell>
      <TableCell width="500px" padding="none" sx={{ p: 0.5 }}>
        <Grid container size={12} sx={{ width: "100%", pt: 0.5, pb: 0.5 }} spacing={1}>
          {fonction?.displayFields?.territoire === true && (
            <ControlledTerritoireAutoComplete
              name="territoire"
              label="Territoire"
              territoires={territoires}
              isLoading={isLoading}
            />
          )}
          {fonction?.displayFields?.perimetre === true && (
            <ControlledPerimetreAutoComplete
              name="perimetre"
              label="Périmètre"
              perimetres={availablePerimetres}
              isLoading={isLoading}
            />
          )}
          {fonction?.displayFields?.commune === true && (
            <ControlledCommuneAutoComplete
              name="commune"
              variant="commune"
              communes={communes}
              isLoading={isLoading}
            />
          )}
        </Grid>
      </TableCell>
      <TableCell width="250px" padding="none" sx={{ p: 0.5 }}>
        <Grid container size={12} sx={{ width: "100%", pt: 0.5, pb: 0.5 }} spacing={1}>
          <ControlledDateTime
            name="dateDebutMandat"
            label="Début du mandat *"
            rules={{
              required: "Choisir une date de début de mandat est obligatoire",
              ...validDateRules,
            }}
          />
          {variant === "edit" && (
            <ControlledDateTime
              name="dateFinMandat"
              label="Fin du mandat *"
              rules={validDateRules}
              defaultHelperText="Renseignez une date fin pour clôturer le mandat"
            />
          )}
        </Grid>
      </TableCell>
      <TableCell width="120px" sx={{ textAlign: "right" }} padding="none">
        {/* eslint-disable @typescript-eslint/no-misused-promises */}
        {isSending ? (
          <CircularProgress />
        ) : (
          <IconButton onClick={sendData} color="primary">
            <Tooltip title="Enregistrer" placement="bottom">
              <SaveIcon />
            </Tooltip>
          </IconButton>
        )}
        {/* eslint-enable @typescript-eslint/no-misused-promises */}
        <IconButton onClick={cancel} color="error">
          <Tooltip title="Annuler" placement="bottom">
            <ClearIcon />
          </Tooltip>
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

export default MandatSdeaTableRowFormContent;
